/**
 * First touch: which campaign and which outside site brought a company to
 * Zettl, stored once on the company (`organizations.first_touch_*`).
 *
 * What is kept: `utm_source`, `utm_medium`, `utm_campaign` and the referring
 * site's host name (e.g. `www.google.com`), each a short string. Nothing about
 * the person: no full referrer URL, no search terms, no id. It never goes to
 * analytics tools.
 *
 * Only with analytics consent (§89(3) of Act 127/2005, the cookie policy's
 * promise that nothing analytics-related happens before consent):
 * - Without consent the touch lives in memory only (a module variable), and it
 *   may ride in the query of a link from zettl.cz to app.zettl.cz, which is not
 *   storage on the device.
 * - With consent, on either host (they share the consent cookie), it is kept in
 *   this tab's sessionStorage (`zettl:first-touch`) until sign-up; consent given
 *   later in the same tab flushes it from memory then. Withdrawing consent
 *   deletes the key (clearAnalyticsStorage).
 * - Only a stored touch is written to the company, so without consent the
 *   company gets nothing and `first_touch_at` stays null.
 */

export interface FirstTouch {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_host: string | null;
}

export const FIRST_TOUCH_KEY = "zettl:first-touch";

/** Same caps as the database's check constraints. */
const MAX_UTM = 100;
const MAX_HOST = 253;

const OWN_HOST = /(^|\.)(zettl\.cz|zettlapp\.com)$/i;

function clean(value: string | null, max: number): string | null {
  if (!value) return null;
  const v = value.trim();
  if (v === "" || v.length > max) return null;
  // Control characters and markup never belong in a campaign name.
  return /^[\p{L}\p{N} ._+\-/:|()]+$/u.test(v) ? v : null;
}

/** An outside site's host name, or null for none, our own sites, or junk. */
export function referrerHost(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    const host = url.hostname.toLowerCase();
    if (OWN_HOST.test(host) || host === "localhost") return null;
    return clean(host, MAX_HOST);
  } catch {
    return null;
  }
}

/** The first touch a page view carries, or null when it carries nothing. */
export function parseFirstTouch(
  search: string,
  referrer: string | null | undefined
): FirstTouch | null {
  const query = new URLSearchParams(search);
  const touch: FirstTouch = {
    utm_source: clean(query.get("utm_source"), MAX_UTM),
    utm_medium: clean(query.get("utm_medium"), MAX_UTM),
    utm_campaign: clean(query.get("utm_campaign"), MAX_UTM),
    // Carried over from zettl.cz (see appendFirstTouch), else this page's own referrer.
    referrer_host: clean(query.get("ft_ref"), MAX_HOST) ?? referrerHost(referrer),
  };
  return isEmpty(touch) ? null : touch;
}

export function isEmpty(touch: FirstTouch | null): boolean {
  return (
    !touch ||
    (!touch.utm_source && !touch.utm_medium && !touch.utm_campaign && !touch.referrer_host)
  );
}

/** A link to the app with the first touch in its query (only fields not already there). */
export function appendFirstTouch(href: string, touch: FirstTouch | null): string {
  if (isEmpty(touch)) return href;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const pairs: [string, string | null][] = [
    ["utm_source", touch!.utm_source],
    ["utm_medium", touch!.utm_medium],
    ["utm_campaign", touch!.utm_campaign],
    ["ft_ref", touch!.referrer_host],
  ];
  for (const [key, value] of pairs) {
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;

function session(): Store {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function readFirstTouch(store: Store = session()): FirstTouch | null {
  try {
    const raw = store?.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FirstTouch>;
    const touch: FirstTouch = {
      utm_source: clean(parsed.utm_source ?? null, MAX_UTM),
      utm_medium: clean(parsed.utm_medium ?? null, MAX_UTM),
      utm_campaign: clean(parsed.utm_campaign ?? null, MAX_UTM),
      referrer_host: clean(parsed.referrer_host ?? null, MAX_HOST),
    };
    return isEmpty(touch) ? null : touch;
  } catch {
    return null;
  }
}

/** Keeps the first touch of this tab; a later one never replaces it. */
export function rememberFirstTouch(touch: FirstTouch | null, store: Store = session()): void {
  if (isEmpty(touch) || readFirstTouch(store)) return;
  try {
    store?.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  } catch {
    // blocked storage: attribution is a nice-to-have
  }
}

/** Withdrawn consent: the stored touch goes (the in-memory one may still ride a link). */
export function forgetFirstTouch(store: Store = session()): void {
  try {
    store?.removeItem(FIRST_TOUCH_KEY);
  } catch {
    // blocked storage: nothing was stored
  }
}

/** This page view's first touch, held in memory until consent allows storing it. */
let pending: FirstTouch | null = null;

/** The touch this tab knows: stored (with consent) or in memory. */
export function currentFirstTouch(store: Store = session()): FirstTouch | null {
  return readFirstTouch(store) ?? pending;
}

export interface ConsentHooks {
  /** Whether analytics consent is granted right now. */
  analyticsAllowed: () => boolean;
  /** Called with every new choice; true when analytics is allowed. */
  onChange: (listener: (analyticsAllowed: boolean) => void) => unknown;
}

/**
 * Remember this page view's first touch: in memory always, in sessionStorage
 * only once analytics consent is granted (now or later in this tab).
 */
export function captureFirstTouch(
  search: string,
  referrer: string | null | undefined,
  consent: ConsentHooks,
  store: Store = session()
): void {
  pending = pending ?? parseFirstTouch(search, referrer);
  if (consent.analyticsAllowed()) rememberFirstTouch(pending, store);
  consent.onChange((allowed) => {
    if (allowed) rememberFirstTouch(pending, store);
    else forgetFirstTouch(store);
  });
}

/** Tests only. */
export function resetFirstTouchForTests(): void {
  pending = null;
}

/**
 * Called once at start-up in the browser: remember this visit's first touch
 * (stored only with consent), and carry it along on links from the public site
 * to the app host.
 */
export function installFirstTouch(appHost: string, consent: ConsentHooks): void {
  if (typeof window === "undefined") return;
  captureFirstTouch(window.location.search, document.referrer, consent);
  if (window.location.hostname === appHost) return;
  document.addEventListener(
    "click",
    (event) => {
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.hostname !== appHost) return;
      const touch = currentFirstTouch();
      if (touch) anchor.href = appendFirstTouch(anchor.href, touch);
    },
    { capture: true }
  );
}

export interface FirstTouchUpdate {
  first_touch_utm_source: string | null;
  first_touch_utm_medium: string | null;
  first_touch_utm_campaign: string | null;
  first_touch_referrer_host: string | null;
  first_touch_at: string;
}

/**
 * What to write to the new company, or null to write nothing: no analytics
 * consent, or no stored touch. Never a row of nulls.
 */
export function firstTouchUpdate(
  analyticsAllowed: boolean,
  stored: FirstTouch | null,
  now: Date = new Date()
): FirstTouchUpdate | null {
  if (!analyticsAllowed || isEmpty(stored)) return null;
  return {
    first_touch_utm_source: stored!.utm_source,
    first_touch_utm_medium: stored!.utm_medium,
    first_touch_utm_campaign: stored!.utm_campaign,
    first_touch_referrer_host: stored!.referrer_host,
    first_touch_at: now.toISOString(),
  };
}
